// src/components/aiondemand/JobsSection.jsx

import { useEffect } from 'react';
import { Activity, Loader2, CheckCircle2, AlertCircle, Clock } from 'lucide-react';

import { useAIOnDemandStore } from '../../stores/useAIOnDemandStore';
import { useProjectStore } from '../../stores/useProjectStore';
import { getActionById } from '../../actions/registry.js';

export default function JobsSection({ onSelectJob }) {
  const projectId = useProjectStore((s) => s.activeProjectId);
  const jobs = useAIOnDemandStore((s) => s.jobs);
  const jobsLoading = useAIOnDemandStore((s) => s.jobsLoading);
  const loadJobs = useAIOnDemandStore((s) => s.loadJobs);

  useEffect(() => {
    if (projectId) loadJobs(projectId);
  }, [projectId, loadJobs]);

  return (
    <div className="border-b border-slate-800">
      <div className="px-3 py-2 flex items-center gap-1.5">
        <Activity size={11} className="text-fg-secondary" />
        <h3 className="text-[10px] font-semibold tracking-[0.14em] font-mono uppercase text-slate-400">
          Jobs
        </h3>
      </div>
      <div className="pb-3">
        {jobsLoading && jobs.length === 0 ? (
          <p className="px-3 text-[10px] text-fg-secondary italic">Loading…</p>
        ) : jobs.length === 0 ? (
          <p className="px-3 text-[10px] text-fg-secondary italic leading-relaxed">
            No jobs yet. Run a model action to see results here.
          </p>
        ) : (
          <div className="flex flex-col gap-1 px-2">
            {jobs.slice(0, 20).map((job) => (
              <JobCard key={job.id} job={job} onClick={() => onSelectJob(job.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function JobCard({ job, onClick }) {
  const action = getActionById(job.task_key) ?? null;
  const actionName = action?.name ?? humanizeActionId(job.task_key);

  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-2 rounded-md px-2.5 py-2
                 bg-bg border border-slate-800 hover:border-accent-action/40
                 hover:bg-bg-soft transition text-left"
    >
      <StatusBadge status={job.status} />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-slate-200 truncate">
          {actionName}
        </div>
        <div className="text-[10px] text-fg-secondary truncate">
          {summarizeJob(job)}
        </div>
      </div>
    </button>
  );
}

function StatusBadge({ status }) {
  if (status === 'completed') return <CheckCircle2 size={12} className="text-status-busy shrink-0" />;
  if (status === 'failed') return <AlertCircle size={12} className="text-red-400 shrink-0" />;
  if (status === 'running') return <Loader2 size={12} className="text-status-busy animate-spin shrink-0" />;
  if (status === 'queued') return <Clock size={12} className="text-fg-secondary shrink-0" />;
  return <div className="w-3 h-3 rounded-full bg-slate-700 shrink-0" />;
}

function summarizeJob(job) {
  const parts = [];

  // Scope tag
  parts.push(job.aoi_id ? 'AOI' : 'Viewport');

  // Progress for running jobs
  if (job.status === 'running' && job.processed_tile_count != null && job.processed_tile_count > 0) {
    parts.push(`${job.processed_tile_count} tiles`);
  }

  // Time
  if (job.created_at) {
    parts.push(formatRelative(job.created_at));
  }

  return parts.join(' · ');
}

function formatRelative(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function humanizeActionId(id) {
  if (!id) return 'Unknown action';
  return id.split(/[._]/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
}